import { createValidate } from 'typia'
import { validateGuard } from 'components/shared/parsing/parsing.js'
import type { ProductSpecificationGroup } from 'components/discovery/pdp/product-specifications/typing/product-specifications.js'

export type PdpProductTag = {
  as_badge: boolean
  badge_type: string | null
  name: string
  picture?: string | null
}

export type PdpProductBrand = {
  id?: number | null
  name: string | null
  slug?: string | null
  picture?: string | null
}

export type PdpProductRating = {
  value: number | null
  count: number | null
}

export type PdpProductStatus = {
  can_order_product: boolean | null
  email: boolean | null
  message: string | null
}

export type PdpFeaturedAttribute = {
  value: string
  picture: string | null
  display_mode: 'icon' | 'text' | 'icon_text' | null
}

export type PdpVariantAttribute = {
  id: number
  name: string
  value: string
  value_id: number
  picture: string | null
  css_color: string | null
  display_mode: string
}

export type PdpProductVariant = {
  id: number
  name: string
  type: string | null
  attributes: PdpVariantAttribute[]
}

export type PdpVariantsItem = {
  page_link: string
  variant: {
    attributes: {
      name: string
      value: string
      picture: string | null
      css_color: string | null
      display_mode: string
    }[]
  } | null
}

export type PdpStockIndication = {
  rule_id?: string | number
  style: 'success' | 'warning' | 'error' | 'info'
  icon: string
  text: string
  tooltip?: string | null
}

export interface Levy {
  amount: string
  amount_no_format: number | string | null
  description: string
  name: string
}

export const bundleDiscountTypes = ['percentage', 'amount'] as const
export type BundleDiscountType = typeof bundleDiscountTypes[number]

export type PdpBundleProduct = Pick<PdpProduct, 'id' | 'name' | 'picture' | 'page_link'>

export type PdpProductBundle = {
  id: number
  name: string
  description: string
  type: BundleDiscountType
  discount: string
  discount_no_format?: string
  price_normal: string
  price_discount: string
  stock_msg?: string | null
  stock_amount: number
  products: PdpBundleProduct[]
}

export type PdpDiscountBundles = {
  best_discount: string
  bundles: PdpProductBundle[]
}

export type PdpPremiumBundleProduct = Pick<PdpProduct, 'id' | 'name' | 'code' | 'picture' | 'page_link'> & {
  prices: PdpProduct['prices'] & { sale_display: string }
}

export type PdpPremiumBundle = {
  name: string
  description: string
  products: PdpPremiumBundleProduct[]
}

export type PdpPremiumBundles = {
  choose_sub_product: boolean
  premium_bundle: PdpPremiumBundle
}

/** Extra product image in the DAM v2 image sizes */
export type PdpExtraPicture = {
  mini?: string | null
  small?: string | null
  normal?: string | null
  large?: string | null
  alt_text?: string | null
}

/** Product asset from the DAM: images, videos (`video_provider`) and download links (`type: 'url'`) */
export type PdpProductAsset = {
  type: string
  asset: string | null
  name?: string | null
  language?: string | null
  shop?: number | string | null
  alt_text?: string | null
  video_provider?: string | null
}

export type PdpProduct = {
  id: number
  code: string
  code_ean: string | null
  name: string
  page_link: string
  num_variants: number | null
  brand?: PdpProductBrand | null
  rating?: {
    combine_reviews?: boolean | null
    main?: PdpProductRating | null
    variant?: PdpProductRating | null
  } | null
  status?: PdpProductStatus | null
  description?: string | null
  description2?: string | null
  description3?: string | null
  picture: { mini: string; small: string; normal: string; large: string }
  default_picture: { mini: string; small: string; normal: string; large: string }
  extra_pictures?: PdpExtraPicture[]
  assets?: PdpProductAsset[]
  attributes?: ProductSpecificationGroup<string | null>,
  prices: {
    consumer: string | null
    currency_code: string | null
    customer: string | null
    sale: string | null
    sale_taxed: string | null
    list: string | null
    price_per_price_uom?: string | false | null
    price_uom?: string | null
  }
  stock: {
    amount: number | null
    message: string | null
    message2?: string | null
    indication?: PdpStockIndication | null
  }
  ordering: {
    cannot_be_added_to_basket: boolean | null
    default_order_quantity?: number | null
    default_uom: string
    maximum_order_quantity: number | null
    minimum_order_quantity: number | null
    order_quantity: number | null
  }
  tags: PdpProductTag[]
  featured_attributes: PdpFeaturedAttribute[]
  variants: PdpVariantsItem[] | null
  variant: PdpProductVariant | null
  levies: Levy[]
  discount_bundles?: PdpDiscountBundles | null
  premium_bundles?: PdpPremiumBundles | null
}

export type PdpListProduct = Omit<PdpProduct, 'attributes'>

const validate = createValidate<PdpListProduct[]>()

export const parsePdpProducts = (data: unknown): PdpListProduct[] => {
  validateGuard(validate, data, 'pdp products')
  return data
}
